import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useEffect, useMemo, useState, } from "react";
// 🔐 USER-SCOPED THEME KEY
const getUserThemeKey = () => {
    try {
        let email = localStorage.getItem("email") ||
            sessionStorage.getItem("email") ||
            "";
        if (!email) {
            const raw = localStorage.getItem("auth_user") ||
                sessionStorage.getItem("auth_user") ||
                localStorage.getItem("user") ||
                sessionStorage.getItem("user") ||
                "";
            if (raw) {
                const parsed = JSON.parse(raw);
                email = String(parsed?.email || parsed?.user?.email || "").toLowerCase();
            }
        }
        return email ? `ava_theme:${email.toLowerCase()}` : "ava_theme:unknown";
    }
    catch {
        return "ava_theme:unknown";
    }
};
const THEME_ALIASES = {
    cyan: "cyan",
    purple: "purple",
    emerald: "emerald",
    amber: "amber",
    lava: "lava",
    midnight: "midnight",
    aurora: "aurora",
    black: "black",
    red: "lava",
    crimson: "lava",
    blue: "midnight",
    green: "emerald",
    orange: "amber",
    yellow: "amber",
    gold: "amber",
    pink: "aurora",
    white: "cyan",
};
export function normalizeThemeName(value, fallback = "cyan") {
    const normalized = String(value || "")
        .trim()
        .toLowerCase();
    return THEME_ALIASES[normalized] || fallback;
}
// Using user-scoped theme key
const THEME_STORAGE_KEY = getUserThemeKey();
const THEMES = {
    cyan: {
        bg: "radial-gradient(circle at center, #063b44 0%, #031116 48%, #000000 100%)",
        panel: "rgba(5, 24, 30, 0.92)",
        text: "#e7fbfd",
        accent: "#16cfe0",
        avaAccent: "#24dcea",
        accent2: "#5ce3ed",
        border: "rgba(22, 207, 224, 0.30)",
        glow: "0 0 22px rgba(22, 207, 224, 0.26)",
        buttonBg: "rgba(22, 207, 224, 0.11)",
        accentGlow: "rgba(22, 207, 224, 0.36)",
        panelBg: "rgba(5, 24, 30, 0.92)",
        cardBg: "rgba(5, 20, 26, 0.92)",
        mutedText: "rgba(231,251,253,0.66)",
    },
    purple: {
        bg: "radial-gradient(circle at center, #2a1745 0%, #10091b 48%, #000000 100%)",
        panel: "rgba(24, 13, 39, 0.92)",
        text: "#f4edfb",
        accent: "#9b6ddb",
        avaAccent: "#ad82e5",
        accent2: "#c09dec",
        border: "rgba(155, 109, 219, 0.30)",
        glow: "0 0 22px rgba(155, 109, 219, 0.27)",
        buttonBg: "rgba(155, 109, 219, 0.11)",
        accentGlow: "rgba(155, 109, 219, 0.37)",
        panelBg: "rgba(24, 13, 39, 0.92)",
        cardBg: "rgba(21, 12, 34, 0.92)",
        mutedText: "rgba(244,237,251,0.66)",
    },
    emerald: {
        bg: "radial-gradient(circle at center, #123d2a 0%, #07170f 48%, #000000 100%)",
        panel: "rgba(8, 31, 20, 0.92)",
        text: "#e8f8ef",
        accent: "#36b86b",
        avaAccent: "#49c97b",
        accent2: "#78d89a",
        border: "rgba(54, 184, 107, 0.30)",
        glow: "0 0 22px rgba(54, 184, 107, 0.26)",
        buttonBg: "rgba(54, 184, 107, 0.11)",
        accentGlow: "rgba(54, 184, 107, 0.36)",
        panelBg: "rgba(8, 31, 20, 0.92)",
        cardBg: "rgba(7, 27, 18, 0.92)",
        mutedText: "rgba(232,248,239,0.66)",
    },
    amber: {
        bg: "radial-gradient(circle at center, #49320f 0%, #1b1206 48%, #000000 100%)",
        panel: "rgba(37, 25, 8, 0.92)",
        text: "#fbf4e4",
        accent: "#c89232",
        avaAccent: "#d8a744",
        accent2: "#e2bd70",
        border: "rgba(200, 146, 50, 0.30)",
        glow: "0 0 22px rgba(200, 146, 50, 0.25)",
        buttonBg: "rgba(200, 146, 50, 0.11)",
        accentGlow: "rgba(200, 146, 50, 0.35)",
        panelBg: "rgba(37, 25, 8, 0.92)",
        cardBg: "rgba(32, 22, 8, 0.92)",
        mutedText: "rgba(251,244,228,0.66)",
    },
    lava: {
        bg: "radial-gradient(circle at center, #49171b 0%, #1b090b 48%, #000000 100%)",
        panel: "rgba(38, 12, 15, 0.92)",
        text: "#f9e9ea",
        accent: "#c84a52",
        avaAccent: "#d65c64",
        accent2: "#e18489",
        border: "rgba(200, 74, 82, 0.30)",
        glow: "0 0 22px rgba(200, 74, 82, 0.25)",
        buttonBg: "rgba(200, 74, 82, 0.11)",
        accentGlow: "rgba(200, 74, 82, 0.35)",
        panelBg: "rgba(38, 12, 15, 0.92)",
        cardBg: "rgba(33, 11, 13, 0.92)",
        mutedText: "rgba(249,233,234,0.66)",
    },
    midnight: {
        bg: "radial-gradient(circle at center, #172846 0%, #080f1d 48%, #000000 100%)",
        panel: "rgba(12, 23, 42, 0.92)",
        text: "#e9f0fa",
        accent: "#527fbd",
        avaAccent: "#6794ce",
        accent2: "#8badde",
        border: "rgba(82, 127, 189, 0.30)",
        glow: "0 0 22px rgba(82, 127, 189, 0.25)",
        buttonBg: "rgba(82, 127, 189, 0.11)",
        accentGlow: "rgba(82, 127, 189, 0.35)",
        panelBg: "rgba(12, 23, 42, 0.92)",
        cardBg: "rgba(11, 20, 36, 0.92)",
        mutedText: "rgba(233,240,250,0.66)",
    },
    aurora: {
        bg: "radial-gradient(circle at center, #32182e 0%, #071817 48%, #000000 100%)",
        panel: "rgba(8, 31, 30, 0.92)",
        text: "#e9f8f6",
        accent: "#d85fa8",
        avaAccent: "#56c9bd",
        accent2: "#55d6c9",
        border: "rgba(216, 95, 168, 0.32)",
        glow: "0 0 22px rgba(67, 185, 173, 0.25)",
        buttonBg: "rgba(67, 185, 173, 0.11)",
        accentGlow: "rgba(216, 95, 168, 0.38)",
        panelBg: "rgba(8, 31, 30, 0.92)",
        cardBg: "rgba(7, 27, 26, 0.92)",
        mutedText: "rgba(233,248,246,0.66)",
    },
    black: {
        bg: "radial-gradient(circle at center, #090a0c 0%, #030405 58%, #000000 100%)",
        panel: "rgba(5, 6, 8, 0.94)",
        text: "#e5e7eb",
        accent: "#3f4856",
        avaAccent: "#8d98a8",
        accent2: "#606b7b",
        border: "rgba(120, 130, 145, 0.30)",
        glow: "0 0 12px rgba(90, 100, 115, 0.18)",
        buttonBg: "rgba(28, 32, 38, 0.72)",
        accentGlow: "rgba(90, 100, 115, 0.22)",
        panelBg: "rgba(5, 6, 8, 0.94)",
        cardBg: "rgba(7, 8, 10, 0.94)",
        mutedText: "rgba(229,231,235,0.70)",
    },
};
const FALLBACK_THEME = THEMES.cyan;
export const ThemeContext = createContext({
    theme: "cyan",
    setTheme: () => { },
    themeColors: FALLBACK_THEME,
});
function isThemeName(value) {
    return !!value && normalizeThemeName(value) in THEMES;
}
export const ThemeProvider = ({ children }) => {
    const [theme, setThemeState] = useState(() => {
        try {
            const saved = localStorage.getItem(getUserThemeKey()) ||
                localStorage.getItem(getUserThemeKey() + ":color") ||
                localStorage.getItem("ava_theme") ||
                localStorage.getItem("ava_theme_color");
            return normalizeThemeName(saved, "cyan");
        }
        catch {
            return "cyan";
        }
    });
    const themeColors = THEMES[theme] ?? FALLBACK_THEME;
    // === LIVE DOM THEME BRIDGE ===
    useEffect(() => {
        try {
            const root = document.documentElement;
            root.setAttribute("data-theme", theme);
            root.style.setProperty("--ava-bg", themeColors.bg);
            root.style.setProperty("--ava-panel", themeColors.panel);
            root.style.setProperty("--ava-text", themeColors.text);
            root.style.setProperty("--ava-accent", themeColors.accent);
            root.style.setProperty("--ava-accent-2", themeColors.accent2);
            root.style.setProperty("--ava-face-accent", themeColors.avaAccent);
            root.style.setProperty("--ava-border", themeColors.border);
            root.style.setProperty("--ava-accent-glow", themeColors.accentGlow);
            root.style.setProperty("--ava-button", themeColors.accent);
            root.style.setProperty("--ava-card", themeColors.cardBg);
            root.style.setProperty("--ava-muted", themeColors.mutedText);
            root.style.setProperty("--neon-color", themeColors.accent);
            document.body.style.background = themeColors.bg;
            document.body.style.color = themeColors.text;
        }
        catch (err) {
            console.error("Theme bridge error:", err);
        }
    }, [theme, themeColors]);
    const setTheme = (nextTheme) => {
        const safeTheme = normalizeThemeName(nextTheme, "cyan");
        setThemeState(safeTheme);
        try {
            const scopedKey = getUserThemeKey();
            localStorage.setItem(scopedKey, safeTheme);
            localStorage.setItem(scopedKey + ":color", safeTheme);
            window.dispatchEvent(new CustomEvent("ava-theme-change", {
                detail: { color: safeTheme },
            }));
        }
        catch { }
    };
    // === LIVE THEME/AUTH SYNC SYSTEM ===
    const value = useMemo(() => ({ theme, setTheme, themeColors }), [theme, themeColors]);
    return _jsx(ThemeContext.Provider, { value: value, children: children });
};
export default ThemeProvider;
